import React from 'react';

import { Flex, Icon, Link, Text, Tooltip } from '@chakra-ui/react';
import { CheckCircle, MinusCircle, XCircle } from '@phosphor-icons/react';

import OrgRouteLink from 'components/common/OrgRouteLink';

interface PolicyComplianceIndicatorProps {
  isCompliant?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  tooltipText?: string;
  userId: string;
}

export const PolicyComplianceIndicator = ({
  isCompliant,
  size = 'md',
  showLabel = true,
  userId,
}: PolicyComplianceIndicatorProps) => {
  const iconSize = {
    sm: '16px',
    md: '20px',
    lg: '24px',
  };

  const textSize = {
    sm: 'body2',
    md: 'body1',
    lg: 'heading3',
  };

  const getComplianceIcon = (isCompliant?: boolean): { icon; color; label } => {
    if (isCompliant === null || isCompliant === undefined) {
      return {
        icon: MinusCircle,
        color: 'content.feedback.secondary',
        label: 'No Policy Set',
      };
    } else if (isCompliant) {
      return {
        icon: CheckCircle,
        color: 'content.feedback.success.secondary',
        label: 'In Compliance',
      };
    } else {
      return {
        icon: XCircle,
        color: 'content.feedback.error.secondary',
        label: 'Out of Compliance',
      };
    }
  };

  const { icon, color, label } = getComplianceIcon(isCompliant);

  const content = (
    <Flex alignItems="center" gap="2" w="fit-content">
      <Icon as={icon} color={color} weight="fill" boxSize={iconSize[size]} />
      {showLabel && (
        <Text textStyle={textSize[size]} color={color}>
          {label}
        </Text>
      )}
    </Flex>
  );

  if (!showLabel) {
    if (userId) {
      return (
        <Link as={OrgRouteLink} to={`/settings/users/${userId}/edit`}>
          <Tooltip label={label} hasArrow placement="top">
            {content}
          </Tooltip>
        </Link>
      );
    }
    return (
      <Tooltip label={label} hasArrow placement="top">
        {content}
      </Tooltip>
    );
  }

  if (userId) {
    return (
      <Link as={OrgRouteLink} to={`/settings/users/${userId}/edit`}>
        {content}
      </Link>
    );
  }

  return content;
};
